import { Auth } from '@aws-amplify/auth';
import { GraphQLClient } from 'graphql-request';

import type {
  CognitoIApi,
  SignUpInput,
  CognitoResult,
  UserAttributes,
  GetUserInfoResult,
} from './cognitoIApi';

import aws from '@/constants/env';
import { SessionError } from '@/errors/sessionError';

export class CognitoApi implements CognitoIApi {
  async getGraphQLClient(): Promise<GraphQLClient> {
    const token = await this.getIdJwtToken();
    const client = new GraphQLClient(aws.aws_appsync_graphqlEndpoint, {
      headers: {
        Authorization: token,
      },
    });
    return client;
  }

  async getUserInfo(): Promise<GetUserInfoResult> {
    try {
      const user = await Auth.currentAuthenticatedUser();
      if (user.username === undefined || typeof user.username !== 'string') {
        throw new Error();
      }
      const username: string = user.username as string;
      const attributes: UserAttributes = {
        sub: user.attributes.sub,
        address: user.attributes.address,
        email_verified: user.attributes.email_verified,
        name: user.attributes.name,
        nameKana: user.attributes['custom:nameKana'],
        familyNameKana: user.attributes['custom:familyNameKana'],
        postalCode: user.attributes['custom:postalcode'],
        familyName: user.attributes.family_name,
        email: user.attributes.email,
        tel: user.attributes['custom:tel'],
      };
      const result: GetUserInfoResult = {
        username,
        attributes,
      };
      return result;
    } catch (e) {
      let msg = 'SessionError in getUserInfo';
      if (e instanceof Error) {
        msg += `:${e.message}`;
      }
      throw new SessionError(msg);
    }
  }

  async getIdJwtToken() {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const idToken = user.signInUserSession.idToken.jwtToken as string;
      return idToken;
    } catch (e) {
      let msg = 'SessionError in getIdJwtToken';
      if (e instanceof Error) {
        msg += `:${e.message}`;
      }
      throw new SessionError(msg);
    }
  }

  async signIn(username: string, password: string) {
    const result: CognitoResult = { status: 'error', flg: { isConfirmedError: false }, msg: '不明なエラーが起きました' };
    try {
      await Auth.signIn(username, password);
      result.status = 'ok';
      result.msg = 'ログインに成功しました';
    } catch (e: unknown) {
      if (e instanceof Error) {
        if (e.name === 'NotAuthorizedException') {
          result.msg = 'ユーザー名またはパスワードが間違っています';
        } else if (e.name === 'ResourceNotFoundException') {
          result.msg = 'ユーザープールクライアントが存在しません';
        } else if (e.name === 'UserNotFoundException') {
          result.msg = 'ユーザーIDが間違っています';
        } else if (e.name === 'UserNotConfirmedException') {
          result.msg = 'ユーザー認証がされていません。メールアドレスに送られた認証コードを入力することで認証を完了してください。';
          result.flg = { isConfirmedError: true };
        } else {
          result.msg = e.message;
        }
      }
    }
    return result;
  }

  async signOut() {
    await Auth.signOut();
  }

  async signUp(input: SignUpInput) {
    const result: CognitoResult = { status: 'error', msg: '不明なエラーが起きました' };
    try {
      await Auth.signUp({
        username: input.userId,
        password: input.password,
        attributes: {
          email: input.email,
          name: input.name,
          'custom:nameKana': input.nameKana,
          family_name: input.familyName,
          'custom:familyNameKana': input.familyNameKana,
          address: input.address,
          'custom:postalcode': input.postalCode,
          'custom:tel': input.tel,
        },
      });
      result.status = 'ok';
      result.msg = 'メールアドレスに認証コードを送りました。認証を完了させてください。迷惑メールフォルダも確認してください。';
    } catch (e: unknown) {
      if (e instanceof Error) {
        if (e.name === 'UserLambdaValidationException') {
          if (e.message === 'PreSignUp failed with error invalid userName.') {
            result.msg = '使用できないユーザIDです';
          } else if (e.message === 'PreSignUp failed with error the email exist.') {
            result.msg = '指定のメールアドレスはすでに使用されています';
          } else {
            result.msg = e.message;
          }
        } else if (e.name === 'NotAuthorizedException') {
          result.msg = 'クライアントが不正な属性の書き込みを試みました';
        } else if (e.name === 'UsernameExistsException') {
          result.msg = `ユーザー「${input.userId}」はすでに使用されています。別のIDを指定してください。`;
        } else {
          result.msg = e.message;
        }
      }
    }
    return result;
  }

  async resendSignUp(username: string): Promise<CognitoResult> {
    const result: CognitoResult = { status: 'error', msg: '不明なエラーが起きました' };
    const response = await Auth.resendSignUp(username);
    if (typeof response.CodeDeliveryDetails.Destination === 'string') {
      const destination: string = response.CodeDeliveryDetails.Destination as string;
      result.status = 'ok';
      result.msg = `メールアドレス「${destination}」に認証コードを送りました。迷惑メールフォルダもチェックしてください。`;
    } else {
      result.msg = 'メール送信に失敗しました';
    }
    return result;
  }

  async confirmSignUp(username: string, code: string): Promise<CognitoResult> {
    const result: CognitoResult = { status: 'error', msg: '不明なエラーが起きました' };
    try {
      const data = await Auth.confirmSignUp(username, code);
      if (data === 'SUCCESS') {
        result.status = 'ok';
        result.msg = '認証に成功しました。再度ログインしてください。';
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        if (e.name === 'CodeMismatchException') {
          result.msg = '認証コードが間違っています。認証コードが正しいことを確認してください。また、認証コードを再送することでリセットできます。';
        } else if (e.name === 'UserNotFoundException') {
          result.msg = 'ユーザーが存在しません';
        } else {
          result.msg = e.message;
        }
      }
    }
    return result;
  }

  async forgotPassword(username: string): Promise<CognitoResult> {
    const result: CognitoResult = { status: 'error', msg: '不明なエラーが起きました' };
    try {
      const { CodeDeliveryDetails } = await Auth.forgotPassword(username);
      if (typeof CodeDeliveryDetails.Destination === 'string') {
        const email = CodeDeliveryDetails.Destination as string;
        result.status = 'ok';
        result.msg = `確認メールを${email}に送信しました`;
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        if (e.name === 'LimitExceededException') {
          result.msg = '試行回数が上限に達しました。しばらく時間をおいて再度実行してください。';
        } else if (e.name === 'InvalidParameterException') {
          result.msg = 'メールアドレスが承認されていません。管理者に問い合わせてください。';
        } else if (e.name === 'UserNotFoundException') {
          result.msg = 'ユーザーが存在しません';
        } else {
          result.msg = e.message;
        }
      }
    }
    return result;
  }

  async forgotPasswordSubmit(
    username: string,
    code: string,
    password: string,
  ): Promise<CognitoResult> {
    const result: CognitoResult = { status: 'error', msg: '不明なエラーが起きました' };
    try {
      await Auth.forgotPasswordSubmit(username, code, password);
      result.status = 'ok';
      result.msg = 'パスワードを変更しました';
    } catch (e: unknown) {
      if (e instanceof Error) {
        if (e.name === 'CodeMismatchException') {
          result.msg = '認証コードが間違っています';
        } else if (e.name === 'ExpiredCodeException') {
          result.msg = '認証コードの期限が切れています。再度発行してください。';
        } else if (e.name === 'LimitExceededException') {
          result.msg = '試行回数が上限に達しました。しばらく時間をおいて再度実行してください。';
        } else {
          result.msg = e.message;
        }
      }
    }
    return result;
  }

  async changeEmail(
    email: string,
  ): Promise<CognitoResult> {
    const result: CognitoResult = { status: 'error', msg: '不明なエラーが起きました' };
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(user, {
        email,
      });
      result.status = 'ok';
      result.msg = 'メールアドレスを変更し、認証コードを送りました。ログインをしてメールアドレスの検証をしてください。';
    } catch (e: unknown) {
      if (e instanceof Error) {
        result.msg = e.message;
      }
    }
    return result;
  }

  async resendChangeEmail(): Promise<CognitoResult> {
    const result: CognitoResult = { status: 'error', msg: '不明なエラーが起きました' };
    try {
      await Auth.verifyCurrentUserAttribute('email');
      result.status = 'ok';
      result.msg = '認証コードを送りました。迷惑メールフォルダもチェックしてください。';
    } catch (e: unknown) {
      if (e instanceof Error) {
        if (e.name === 'LimitExceededException') {
          result.msg = '試行回数が上限に達しました。しばらく時間をおいて再度実行してください。';
        } else {
          result.msg = e.message;
        }
      }
    }
    return result;
  }

  async verifyEmail(
    code: string,
  ): Promise<CognitoResult> {
    const result: CognitoResult = { status: 'error', msg: '不明なエラーが起きました' };
    try {
      const user = await Auth.currentAuthenticatedUser();
      const response = await Auth.verifyUserAttributeSubmit(user, 'email', code);
      if (response === 'SUCCESS') {
        result.status = 'ok';
        result.msg = 'メールアドレスの検証に成功しました';
      } else {
        result.status = 'error';
        result.msg = '検証に失敗しました';
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        if (e.name === 'CodeMismatchException') {
          result.msg = '認証コードが間違っています';
        } else if (e.name === 'ExpiredCodeException') {
          result.msg = '認証コードの期限が切れています。再度発行してください。';
        } else if (e.name === 'LimitExceededException') {
          result.msg = '試行回数が上限に達しました。しばらく時間をおいて再度実行してください。';
        } else {
          result.msg = e.message;
        }
      }
    }
    return result;
  }
}
